import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "wouter";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { clearDraft, readDraft, useDraftAutosave } from "@/hooks/useDraftAutosave";
import {
  FileText, Plus, Trash2, ArrowLeft, Loader2, XCircle,
  Image as ImageIcon, Video, FileType2,
} from "lucide-react";
import { WhatsAppPreview, type Button_, type TemplateForm } from "@/components/templates/WhatsAppPreview";
import { MediaUploadZone } from "@/components/templates/MediaUploadZone";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import { normalizarNomeDoModelo } from "@shared/whatsapp-template-name";

const emptyForm: TemplateForm = {
  name: "", category: "MARKETING", language: "pt_BR",
  headerType: "none", headerContent: "", bodyText: "",
  footerText: "", buttons: [], variableCount: 0, notes: "",
};

export default function TemplateEditor() {
  const params = useParams<{ id?: string }>();
  const editId = params.id ? Number(params.id) : null;
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();

  const [form, setForm] = useState<TemplateForm>(emptyForm);
  const [uploadingHeader, setUploadingHeader] = useState(false);

  /**
   * O Assistente escreve o template aberto. Só preenche o formulário — quem
   * salva e submete à Meta é a pessoa, porque template reprovado conta contra
   * a qualidade do número.
   */
  useContextoDaTela(
    {
      titulo: editId ? `Template #${editId}: ${form.name || "sem nome"}` : "Novo template do WhatsApp",
      dados: {
        templateId: editId,
        nome: form.name,
        categoria: form.category,
        idioma: form.language,
        cabecalho: form.headerType === "TEXT" ? form.headerContent : form.headerType,
        corpo: form.bodyText,
        rodape: form.footerText,
        botoes: (form.buttons ?? []).map((b) => b.text),
        variaveis: form.variableCount,
      },
      acoes: [
        {
          id: "preencher_template",
          descricao:
            "Preenche os campos do template aberto. Regras da Meta: nome em minúsculas com underline, variáveis posicionais ({{1}}), corpo não começa nem termina com variável.",
          parametros: {
            nome: "nome interno",
            corpo: "texto da mensagem",
            cabecalho: "cabeçalho de texto",
            rodape: "rodapé",
            botoes: "lista de textos de botão (resposta rápida)",
          },
        },
      ],
    },
    {
      preencher_template: (args) => {
        setForm((f) => {
          const botoes = Array.isArray(args.botoes)
            ? (args.botoes as unknown[]).slice(0, 3).map((t) => ({ type: "QUICK_REPLY" as const, text: String(t) }))
            : f.buttons;
          const corpo = args.corpo !== undefined ? String(args.corpo) : f.bodyText;
          // A Meta conta as variáveis pelo maior índice usado, como a tela.
          const indices = [...corpo.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
          return {
            ...f,
            name: args.nome !== undefined ? normalizarNomeDoModelo(String(args.nome)) : f.name,
            bodyText: corpo,
            headerType: args.cabecalho !== undefined ? "TEXT" : f.headerType,
            headerContent: args.cabecalho !== undefined ? String(args.cabecalho) : f.headerContent,
            footerText: args.rodape !== undefined ? String(args.rodape) : f.footerText,
            buttons: botoes as typeof f.buttons,
            variableCount: indices.length > 0 ? Math.max(...indices) : 0,
          };
        });
      },
    },
  );

  // Carregar template existente para edição
  const { data: existingTemplate, isLoading: loadingTemplate } = trpc.templates.get.useQuery(
    { id: editId! },
    { enabled: editId !== null }
  );

  // Chave de rascunho: estável por template (id) ou genérica pra novos.
  const draftKey = useMemo(
    () => `template-editor:${editId ?? "new"}`,
    [editId]
  );

  useEffect(() => {
    if (existingTemplate) {
      setForm({
        name: existingTemplate.name,
        category: existingTemplate.category as any,
        language: existingTemplate.language,
        headerType: (existingTemplate.headerType as any) ?? "none",
        headerContent: existingTemplate.headerContent ?? "",
        bodyText: existingTemplate.bodyText,
        footerText: existingTemplate.footerText ?? "",
        buttons: (existingTemplate.buttons as Button_[]) ?? [],
        variableCount: existingTemplate.variableCount ?? 0,
        notes: existingTemplate.notes ?? "",
      });
    }
  }, [existingTemplate]);

  // Restauração de rascunho: ao montar, oferece restaurar se existir um
  // draft não-vazio. Só fazemos uma vez por mount.
  useEffect(() => {
    const draft = readDraft<TemplateForm>(draftKey);
    if (!draft) return;
    const isEmpty = !draft.name && !draft.bodyText && !draft.headerContent;
    if (isEmpty) { clearDraft(draftKey); return; }
    toast("Rascunho não salvo encontrado", {
      description: "Recuperar o último estado em edição?",
      duration: 15000,
      action: { label: "Restaurar", onClick: () => setForm(draft) },
      cancel: { label: "Descartar", onClick: () => clearDraft(draftKey) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Autosave do form em edição. Considera "sujo" enquanto o usuário está
  // mexendo (form diferente de emptyForm e diferente do snapshot servidor).
  const isDirty = useMemo(() => {
    if (existingTemplate) {
      return (
        form.name !== existingTemplate.name ||
        form.bodyText !== existingTemplate.bodyText ||
        form.headerContent !== (existingTemplate.headerContent ?? "") ||
        form.footerText !== (existingTemplate.footerText ?? "")
      );
    }
    return form.name !== "" || form.bodyText !== "" || form.headerContent !== "";
  }, [form, existingTemplate]);

  useDraftAutosave({ key: draftKey, value: form, enabled: isDirty });

  const createMut = trpc.templates.create.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate();
      clearDraft(draftKey);
      toast.success("Template criado!");
      navigate("/templates");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const updateMut = trpc.templates.update.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate();
      utils.templates.get.invalidate({ id: editId! });
      clearDraft(draftKey);
      toast.success("Template atualizado!");
      navigate("/templates");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const uploadHeaderMut = trpc.templates.uploadHeaderMedia.useMutation();

  const handleHeaderFile = async (file: File) => {
    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Arquivo muito grande (máx ${maxSizeMb}MB)`);
      return;
    }
    setUploadingHeader(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1] ?? "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { url } = await uploadHeaderMut.mutateAsync({
        mediaBase64: base64,
        fileName: file.name,
        mimeType: file.type,
      });
      setForm((f) => ({ ...f, headerContent: url }));
      toast.success("Mídia enviada");
    } catch (err: any) {
      toast.error(toUserFriendlyError(err, "Falha no upload"));
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleSave = () => {
    const name = normalizarNomeDoModelo(form.name);
    if (!name || !form.bodyText) {
      toast.error("Nome e corpo são obrigatórios");
      return;
    }
    if (form.headerType !== "none" && form.headerType !== "TEXT" && !form.headerContent) {
      toast.error(`Faça upload do arquivo de ${form.headerType.toLowerCase()} antes de salvar`);
      return;
    }
    const payload = {
      ...form,
      name,
      headerType: (form.headerType === "none" || !form.headerType) ? null : form.headerType,
      headerContent: form.headerContent || null,
      footerText: form.footerText || null,
      buttons: form.buttons.length > 0 ? form.buttons : null,
      notes: form.notes || null,
    };
    if (editId) {
      updateMut.mutate({ id: editId, ...payload });
    } else {
      createMut.mutate(payload);
    }
  };

  const addButton = () => setForm(f => ({ ...f, buttons: [...f.buttons, { type: "QUICK_REPLY", text: "" }] }));
  const updateButton = (i: number, data: Partial<Button_>) =>
    setForm(f => ({ ...f, buttons: f.buttons.map((b, idx) => idx === i ? { ...b, ...data } : b) }));
  const removeButton = (i: number) =>
    setForm(f => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const bodyCharCount = form.bodyText.length;
  const bodyMaxChars = 1024;
  const isPending = createMut.isPending || updateMut.isPending;

  if (editId !== null && loadingTemplate) {
    return (
      <PermissionGuard permission="whatsapp:manage_templates">
        <DashboardLayout>
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </DashboardLayout>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard permission="whatsapp:manage_templates">
    <DashboardLayout>
      {/* Header sticky com ações principais */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 mb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/templates")}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Voltar
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">
                {editId ? "Editar template" : "Novo template"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {editId ? "Edição salva sobrescreve o atual." : "O template precisa ser aprovado pela Meta antes de ser usado em disparos."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => navigate("/templates")} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Salvando</> : "Salvar template"}
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo: 2 colunas em desktop, 1 coluna em mobile */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-6 pb-12">
        {/* Coluna do formulário */}
        <div className="space-y-6 min-w-0">
          {/* Identificação */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <h2 className="text-base font-semibold">Identificação</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Nome e categoria definem como o template aparece na Meta.</p>
              </div>
              <div>
                <Label>Nome do template <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="boas_vindas_contadores"
                  value={form.name}
                  // Guarda o que foi digitado e só normaliza ao sair do campo:
                  // reescrever a cada tecla quebra o acento de tecla morta
                  // ("promoção" virava "promoc_ao" no teclado ABNT2).
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  onBlur={() => setForm(f => ({ ...f, name: normalizarNomeDoModelo(f.name) }))}
                  className="mt-1.5"
                  disabled={editId !== null}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {form.name && normalizarNomeDoModelo(form.name) !== form.name
                    ? <>Vai para a Meta como <span className="font-mono">{normalizarNomeDoModelo(form.name) || "—"}</span>.</>
                    : "Acentos, espaços e símbolos viram letras simples e underscore, como a Meta exige."}{" "}
                  {editId && "Não pode ser alterado depois de criado."}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v as any }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MARKETING">Marketing</SelectItem>
                      <SelectItem value="UTILITY">Utilidade</SelectItem>
                      <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Idioma</Label>
                  <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pt_BR">Português (BR)</SelectItem>
                      <SelectItem value="en_US">English (US)</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cabeçalho */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <h2 className="text-base font-semibold">Cabeçalho</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Opcional. Aparece destacado no topo da mensagem.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { value: "none", label: "Nenhum", icon: XCircle },
                  { value: "TEXT", label: "Texto", icon: FileText },
                  { value: "IMAGE", label: "Imagem", icon: ImageIcon },
                  { value: "VIDEO", label: "Vídeo", icon: Video },
                  { value: "DOCUMENT", label: "Documento", icon: FileType2 },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, headerType: opt.value as any, headerContent: opt.value === f.headerType ? f.headerContent : "" }))}
                    className={`rounded-lg border py-3 px-2 flex flex-col items-center gap-1.5 transition-all
                      ${form.headerType === opt.value
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-muted hover:border-muted-foreground/30 hover:bg-muted/40"
                      }`}
                  >
                    <opt.icon className="h-4 w-4" />
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
              {form.headerType === "TEXT" && (
                <Input
                  placeholder="Texto do cabeçalho (até 60 caracteres)"
                  value={form.headerContent}
                  maxLength={60}
                  onChange={(e) => setForm(f => ({ ...f, headerContent: e.target.value }))}
                />
              )}
              {(form.headerType === "IMAGE" || form.headerType === "VIDEO" || form.headerType === "DOCUMENT") && (
                <MediaUploadZone
                  type={form.headerType}
                  value={form.headerContent}
                  onUpload={handleHeaderFile}
                  uploading={uploadingHeader}
                />
              )}
            </CardContent>
          </Card>

          {/* Corpo */}
          <Card>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold">Corpo da mensagem <span className="text-destructive">*</span></h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Texto principal. Use variáveis dinâmicas para personalizar por contato.</p>
                </div>
                <span className={`text-xs ${bodyCharCount > bodyMaxChars ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                  {bodyCharCount}/{bodyMaxChars}
                </span>
              </div>
              <Textarea
                placeholder="Olá {{1}}, temos uma novidade para você..."
                value={form.bodyText}
                onChange={(e) => setForm(f => ({ ...f, bodyText: e.target.value }))}
                className="min-h-[160px] font-mono text-sm"
              />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span><code className="bg-muted px-1.5 py-0.5 rounded text-emerald-700 font-mono">{"{{1}}"}</code> variáveis</span>
                <span><code className="bg-muted px-1.5 py-0.5 rounded font-mono">*x*</code> negrito</span>
                <span><code className="bg-muted px-1.5 py-0.5 rounded font-mono">_x_</code> itálico</span>
                <span><code className="bg-muted px-1.5 py-0.5 rounded font-mono">~x~</code> riscado</span>
              </div>
            </CardContent>
          </Card>

          {/* Rodapé */}
          <Card>
            <CardContent className="p-6 space-y-3">
              <div>
                <h2 className="text-base font-semibold">Rodapé</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Opcional. Linha pequena ao final, normalmente para identificar a empresa.</p>
              </div>
              <Input
                placeholder="Ex: Lead Conversa · Resposta automática"
                value={form.footerText}
                maxLength={60}
                onChange={(e) => setForm(f => ({ ...f, footerText: e.target.value }))}
              />
            </CardContent>
          </Card>

          {/* Botões */}
          <Card>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold">Botões ({form.buttons.length}/3)</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Resposta rápida, link ou telefone. Máximo 3 botões.</p>
                </div>
                {form.buttons.length < 3 && (
                  <Button variant="outline" size="sm" onClick={addButton}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar
                  </Button>
                )}
              </div>
              {form.buttons.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">
                  Nenhum botão. Clique em "Adicionar" para criar.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.buttons.map((btn, i) => (
                    <div key={i} className="rounded-lg border bg-muted/20 p-3 space-y-2">
                      <div className="flex gap-2 items-center">
                        <Select value={btn.type} onValueChange={(v) => updateButton(i, { type: v as any })}>
                          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="QUICK_REPLY">Resposta rápida</SelectItem>
                            <SelectItem value="URL">Link</SelectItem>
                            <SelectItem value="PHONE_NUMBER">Telefone</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Texto do botão (máx 25)"
                          maxLength={25}
                          value={btn.text}
                          onChange={(e) => updateButton(i, { text: e.target.value })}
                          className="flex-1"
                        />
                        <Button variant="ghost" size="icon" className="shrink-0 text-destructive hover:text-destructive" onClick={() => removeButton(i)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {btn.type === "URL" && (
                        <Input placeholder="https://..." value={btn.url ?? ""} onChange={(e) => updateButton(i, { url: e.target.value })} />
                      )}
                      {btn.type === "PHONE_NUMBER" && (
                        <Input placeholder="5511999999999" value={btn.phoneNumber ?? ""} onChange={(e) => updateButton(i, { phoneNumber: e.target.value })} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Coluna do preview (sticky em desktop) */}
        <div className="xl:sticky xl:top-24 xl:self-start space-y-3">
          <div>
            <h2 className="text-base font-semibold">Preview WhatsApp</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Atualizado em tempo real.</p>
          </div>
          <WhatsAppPreview form={form} />
          <p className="text-xs text-muted-foreground">
            Variáveis <code className="bg-muted px-1.5 py-0.5 rounded text-emerald-600 font-mono">{"{{N}}"}</code> são substituídas no envio com dados do contato.
          </p>
        </div>
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
